import { Box, type SxProps, type Theme, useTheme } from "@mui/material";
import {
	type QueryKey,
	useIsFetching,
	useQueryClient,
} from "@tanstack/react-query";
import type { PropsWithChildren } from "react";
import { useEffect, useRef, useState } from "react";
import { useSwipeable } from "react-swipeable";
import { SegmentedSpinner } from "web_myplann/Shared/Components/SegmentedSpinner";
import { getScrollContainer } from "web_myplann/Shared/Utils/getScrollContainer";

export interface NativePullToRefreshProps {
	threshold?: number;
	maxPullDistance?: number;
	sx?: SxProps<Theme>;
	queryKeys?: QueryKey[];
	spinnerOffset?: number;
	backgroundColor?: string;
	disableContentPush?: boolean;
	spinnerColor?: string;
}

export const NativePullToRefresh = ({
	children,
	threshold = 40,
	maxPullDistance = 60,
	sx,
	queryKeys,
	spinnerOffset = 0,
	backgroundColor,
	disableContentPush = false,
	spinnerColor,
}: PropsWithChildren<NativePullToRefreshProps>) => {
	const theme = useTheme();
	const queryClient = useQueryClient();
	const isFetching = useIsFetching();
	const [pullDistance, setPullDistance] = useState(0);
	const [isReleasing, setIsReleasing] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const handleRefresh = () => {
		setIsLoading(true);
		if (queryKeys && queryKeys.length > 0) {
			queryKeys.forEach((queryKey) => {
				queryClient.invalidateQueries({
					queryKey,
					refetchType: "active",
				});
			});
		} else {
			queryClient.invalidateQueries({
				refetchType: "active",
				stale: true,
			});
		}
	};

	useEffect(() => {
		if (isLoading && isFetching === 0) {
			setIsLoading(false);
		}
	}, [isLoading, isFetching]);

	useEffect(() => {
		if (!isLoading && isReleasing) {
			setIsReleasing(false);
			setPullDistance(0);
		}
	}, [isLoading, isReleasing]);

	useEffect(() => {
		if (isReleasing || isLoading) {
			document.body.style.overflow = "hidden";
			document.body.style.touchAction = "none";
		} else {
			document.body.style.overflow = "";
			document.body.style.touchAction = "";
		}

		return () => {
			document.body.style.overflow = "";
			document.body.style.touchAction = "";
		};
	}, [isReleasing, isLoading]);

	const applyResistance = (distance: number): number => {
		if (distance <= 0) return 0;

		if (distance <= threshold) {
			return distance;
		}

		const excess = distance - threshold;
		const resistedExcess = excess * 0.4;
		return Math.min(threshold + resistedExcess, maxPullDistance);
	};

	const isAtTop = (): boolean => {
		const scrollEl = getScrollContainer(containerRef.current);
		if (scrollEl) return scrollEl.scrollTop === 0;

		const scrollY = window.scrollY || window.pageYOffset;
		const docScrollTop = document.documentElement.scrollTop;
		const bodyScrollTop = document.body.scrollTop;
		return scrollY === 0 && docScrollTop === 0 && bodyScrollTop === 0;
	};

	const handlers = useSwipeable({
		onSwiping: (eventData) => {
			if (!isAtTop() || isLoading || eventData.dir !== "Down") {
				return;
			}

			const distance = Math.max(0, eventData.deltaY);
			const resistedDistance = applyResistance(distance);
			const clampedDistance = Math.min(resistedDistance, maxPullDistance);

			setPullDistance(clampedDistance);
			setIsReleasing(false);
		},
		onSwipedUp: () => {
			setPullDistance(0);
		},
		onSwiped: (eventData) => {
			if (eventData.dir !== "Down" || isLoading) {
				setPullDistance(0);
				return;
			}

			if (pullDistance >= threshold) {
				setPullDistance(threshold);
				setIsReleasing(true);
				handleRefresh();
			} else {
				setPullDistance(0);
			}
		},
		trackTouch: true,
		trackMouse: false,
		delta: 10,
	});

	const translateY = isReleasing || isLoading ? threshold : pullDistance;

	const isActive = pullDistance > 0 || isReleasing || isLoading;

	const showSpinner = pullDistance > 20 || isReleasing || isLoading;

	const spinnerOpacity =
		isReleasing || isLoading ? 1 : Math.min(pullDistance / threshold, 1);

	return (
		<Box
			{...handlers}
			ref={(el: HTMLDivElement | null) => {
				handlers.ref(el);
				containerRef.current = el;
			}}
			sx={{
				width: "100%",
				position: "relative",
				...sx,
			}}
		>
			{backgroundColor && (
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						height: isActive ? translateY : 0,
						backgroundColor,
						zIndex: 0,
						transition:
							pullDistance === 0 && !isReleasing
								? "height 0.3s ease-out"
								: "none",
					}}
				/>
			)}

			{showSpinner && (
				<Box
					sx={{
						position: "absolute",
						top: Math.max(translateY + spinnerOffset, 0),
						left: "50%",
						transform: "translateX(-50%)",
						opacity: spinnerOpacity,
						transition:
							isReleasing || isLoading ? "none" : "opacity 0.2s ease-out",
						pointerEvents: "none",
						zIndex: 1,
					}}
				>
					<SegmentedSpinner
						size={32}
						color={spinnerColor ?? theme.palette.common.black}
					/>
				</Box>
			)}

			<Box
				sx={{
					paddingTop: disableContentPush ? 0 : `${translateY}px`,
					transition:
						disableContentPush || (pullDistance === 0 && !isReleasing)
							? "padding-top 0.3s ease-out"
							: "none",
				}}
			>
				{children}
			</Box>
		</Box>
	);
};
